/**
 * Build a JSS style fragment that sizes `property` with a `dvh`-based value on
 * browsers that support the dynamic viewport unit, falling back to a `vh`-based
 * value on those that don't (Safari <15.4 etc.) so the element never collapses.
 *
 * `dvh` tracks the *visible* viewport, whereas `vh` resolves against the larger
 * frame (mobile browser toolbars hidden) — the difference is why `vh`-sized
 * dialogs extend below the fold on iPad and feel scrollable (#21357 / #21015).
 *
 * The `@supports` query feature-detects the *same* CSS property it sets, per the
 * `@supports` spec, and the camelCase property name is converted to its
 * kebab-case CSS form for the query.
 *
 * @example
 * paper: {
 *   ...dvhWithFallback('height', 'calc(100dvh - 96px)', 'calc(100vh - 96px)'),
 *   maxHeight: 760
 * }
 * // → { height: 'calc(100vh - 96px)',
 * //     '@supports (height: calc(100dvh - 96px))': { height: 'calc(100dvh - 96px)' } }
 */
export function dvhWithFallback(property: string, dvhValue: string, fallbackValue: string) {
  const cssProperty = property.replaceAll(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)

  return {
    [property]: fallbackValue,
    [`@supports (${cssProperty}: ${dvhValue})`]: {
      [property]: dvhValue
    }
  }
}

export default dvhWithFallback
